import {
  Children,
  createContext,
  useContext,
  type LiHTMLAttributes,
  type OlHTMLAttributes,
  type ReactNode,
} from 'react';
import styled from 'styled-components';
import { colors } from '../../../../styles/colors';
import { fontFamily, headingLevel, radii, space, text } from '../../../../styles/onboardingUiTokens';
import { Body } from '../typography/Body';

const timelineRailWidth = '2.75rem';
/** Figma `Number` is 32px (ERS-61). */
const timelineCircleSize = '2rem';
const timelineLineWidth = '2px';

type ListTimelineContextValue = {
  stepNumber: number;
  isFirst: boolean;
  isLast: boolean;
};

const ListTimelineContext = createContext<ListTimelineContextValue | null>(null);

type ListTimelineLayoutContextValue = {
  columnGap: string;
  rowGap: string;
  showConnectorLine: boolean;
};

const ListTimelineLayoutContext = createContext<ListTimelineLayoutContextValue | null>(null);

function useListTimelineContext(): ListTimelineContextValue {
  const ctx = useContext(ListTimelineContext);
  if (!ctx) {
    throw new Error('`ListTimelineItem` must be used inside `ListTimeline`.');
  }
  return ctx;
}

function useListTimelineLayoutContext(): ListTimelineLayoutContextValue {
  const ctx = useContext(ListTimelineLayoutContext);
  if (!ctx) {
    throw new Error('`ListTimelineItem` must be used inside `ListTimeline`.');
  }
  return ctx;
}

const StyledOl = styled.ol<{ $rowGap: string }>`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: ${({ $rowGap }) => $rowGap};
`;

const StyledLi = styled.li<{ $columnGap: string }>`
  display: flex;
  align-items: flex-start;
  gap: ${({ $columnGap }) => $columnGap};
  margin: 0;
  padding: 0;
`;

const timelineCircleTopNudge = '2px';
/** Vertical position of the step circle center from the top of the rail (matches `margin-top` + half size). */
const timelineCircleCenterY = `calc(${timelineCircleTopNudge} + ${timelineCircleSize} / 2)`;

const Rail = styled.div`
  position: relative;
  flex: 0 0 ${timelineRailWidth};
  width: ${timelineRailWidth};
  align-self: stretch;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  min-height: ${timelineCircleSize};
`;

const RailLineUp = styled.span`
  position: absolute;
  left: 50%;
  top: 0;
  height: ${timelineCircleCenterY};
  width: ${timelineLineWidth};
  transform: translateX(-50%);
  background: ${colors.light};
  z-index: 0;
`;

const RailLineDown = styled.span<{ $rowGap: string }>`
  position: absolute;
  left: 50%;
  top: ${timelineCircleCenterY};
  bottom: calc(-1 * (${({ $rowGap }) => $rowGap}));
  width: ${timelineLineWidth};
  transform: translateX(-50%);
  background: ${colors.light};
  z-index: 0;
`;

const StepCircle = styled.span<{
  $background: string;
  $borderColor: string;
  $numberColor: string;
}>`
  position: relative;
  z-index: 1;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: ${timelineCircleTopNudge};
  width: ${timelineCircleSize};
  height: ${timelineCircleSize};
  border-radius: ${radii.pill};
  border: 1px solid ${({ $borderColor }) => $borderColor};
  background: ${({ $background }) => $background};
  color: ${({ $numberColor }) => $numberColor};
  font-family: ${headingLevel[3].fontFamily};
  font-size: ${headingLevel[3].fontSize};
  line-height: 1;
  font-weight: ${headingLevel[3].fontWeight};
`;

const ContentStack = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${space.xxxs};
  padding-top: 2px;
`;

const LabelText = styled.p<{ $color: string }>`
  margin: 0;
  font-family: ${fontFamily.ubuntu};
  font-size: ${text.overline.fontSize};
  line-height: ${text.overline.lineHeight};
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ $color }) => $color};
`;

/** Figma tags the step title "H2", but that token is Ubuntu Medium 17px — our `H3` heading level. */
const TitleText = styled.p<{ $color: string }>`
  margin: 0;
  font-family: ${headingLevel[3].fontFamily};
  font-size: ${headingLevel[3].fontSize};
  line-height: ${headingLevel[3].lineHeight};
  font-weight: ${headingLevel[3].fontWeight};
  letter-spacing: ${headingLevel[3].letterSpacing};
  color: ${({ $color }) => $color};
`;

/** Marker well fill for a non-highlighted step — Figma `Number` inactive, confirmed as `Light` (#EFF1F1). */
const timelineDefaultCircleBackground = colors.light;

export type ListTimelineItemProps = Omit<LiHTMLAttributes<HTMLLIElement>, 'title'> & {
  children: ReactNode;
  /** Replaces the default numbered step circle. */
  marker?: ReactNode;
  /** Small uppercase line above the title. */
  label?: ReactNode;
  /** Optional heading below the label. */
  title?: ReactNode;
  /**
   * When true, the step marker uses accent fill (see `highlightColor`) and `aria-current="step"`.
   * Pair with `highlightColor` for a one-line brand override.
   */
  highlighted?: boolean;
  /** Marker fill when `highlighted`; defaults to `colors.primary`. */
  highlightColor?: string;
  labelColor?: string;
  titleColor?: string;
  /** Body copy color for `children` (passed through to `Body`). */
  bodyColor?: string;
  numberColor?: string;
  circleBackground?: string;
  circleBorder?: string;
};

function resolveMarker(args: {
  highlighted: boolean;
  highlightColor?: string;
  circleBackground?: string;
  circleBorder?: string;
  numberColor?: string;
}): { background: string; borderColor: string; number: string } {
  const accent = args.highlightColor ?? colors.primary;

  const background = args.circleBackground ?? (args.highlighted ? accent : timelineDefaultCircleBackground);

  const borderColor = args.circleBorder ?? background;

  const number = args.numberColor ?? (args.highlighted ? colors.white : colors.medium);

  return { background, borderColor, number };
}

export function ListTimelineItem({
  marker,
  label,
  title,
  children,
  highlighted = false,
  highlightColor,
  labelColor,
  titleColor,
  bodyColor,
  numberColor,
  circleBackground,
  circleBorder,
  className,
  ...liRest
}: ListTimelineItemProps) {
  const { stepNumber, isFirst, isLast } = useListTimelineContext();
  const { columnGap, rowGap, showConnectorLine } = useListTimelineLayoutContext();
  const hasLabel = label != null && label !== '';
  const hasTitle = title != null && title !== '';

  const markerStyles = resolveMarker({
    highlighted,
    highlightColor,
    circleBackground,
    circleBorder,
    numberColor,
  });

  const labelResolved = labelColor ?? colors.medium;
  const titleResolved = titleColor ?? colors.dark;

  return (
    <StyledLi {...liRest} className={className} aria-current={highlighted ? 'step' : undefined} $columnGap={columnGap}>
      <Rail>
        {showConnectorLine && !isFirst ? <RailLineUp aria-hidden /> : null}
        {showConnectorLine && !isLast ? <RailLineDown aria-hidden $rowGap={rowGap} /> : null}
        <StepCircle
          $background={markerStyles.background}
          $borderColor={markerStyles.borderColor}
          $numberColor={markerStyles.number}
        >
          {marker ?? stepNumber}
        </StepCircle>
      </Rail>
      <ContentStack>
        {hasLabel ? <LabelText $color={labelResolved}>{label}</LabelText> : null}
        {hasTitle ? <TitleText $color={titleResolved}>{title}</TitleText> : null}
        <Body variant="document" tone="muted" style={bodyColor ? { color: bodyColor } : undefined}>
          {children}
        </Body>
      </ContentStack>
    </StyledLi>
  );
}

export type ListTimelineProps = Omit<OlHTMLAttributes<HTMLOListElement>, 'children'> & {
  children: ReactNode;
  rowGap?: string;
  columnGap?: string;
  /** Vertical rail connector between steps. Off by default (Figma, ERS-61 feedback) — opt in per instance. */
  showConnectorLine?: boolean;
};

export function ListTimeline({
  children,
  rowGap = space.md,
  columnGap = space.md,
  showConnectorLine = false,
  start = 1,
  ...olRest
}: ListTimelineProps) {
  const items = Children.toArray(children);
  const layoutValue: ListTimelineLayoutContextValue = { columnGap, rowGap, showConnectorLine };
  const lastIndex = items.length - 1;

  return (
    <ListTimelineLayoutContext.Provider value={layoutValue}>
      <StyledOl {...olRest} $rowGap={rowGap} start={start}>
        {items.map((child, index) => {
          const stepNumber = start + index;
          return (
            <ListTimelineContext.Provider
              key={index}
              value={{ stepNumber, isFirst: index === 0, isLast: index === lastIndex }}
            >
              {child}
            </ListTimelineContext.Provider>
          );
        })}
      </StyledOl>
    </ListTimelineLayoutContext.Provider>
  );
}
